/// <amd-module name="Feed/Legacy/Helpers/UrlHelper" />
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { getAssetLocation, getServiceLocation, routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import * as Url from "VSS/Platform/Util/Url";

import { HubActionStrings, PackageDetailsPivot, SettingsPivotKeys, TfsServiceInstanceId } from "Feed/Common/Constants/Constants";
import { Package } from "Feed/Common/Contracts/Feed";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { getCurrentSelectedProjectName } from "Feed/Legacy/Helpers/ProjectHelper";

const PackagingHubRouteId = "ms.feed.packaging-hub-route";

export function getServiceRootPath(pageContext: IVssPageContext, serviceInstanceId: string): string {
    // service location for onprem is /tfs/DefaultCollection/ and hosted is https://dev.azure.com/<orgname>/
    const url = getServiceLocation(pageContext, serviceInstanceId);
    const isHosted = pageContext.getService<IVssPageService>("IVssPageService").getData().isHosted;
    if (isHosted) {
        return new Url.Uri(url).absoluteUri;
    }

    // in onprem, host is not returned so use the public access mapping in dataprovider to construct url
    const webPageDataService = pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    const publicAccessMapping = webPageDataService.publicAccessMapping()!;
    const publicAccessMappingUrl = new Url.Uri(publicAccessMapping);
    // todo: seems hacky!
    publicAccessMappingUrl.path = "";
    publicAccessMappingUrl.queryString = "";
    publicAccessMappingUrl.hashString = "";

    return Url.combineUrlPaths(publicAccessMappingUrl.absoluteUri, url);
}

function getPackagingHubAbsoluteUrl(pageContext: IVssPageContext): Url.Uri {
    const url: string = routeUrl(pageContext, PackagingHubRouteId, {
        project: getCurrentSelectedProjectName(pageContext)
    });

    return getWebAbsoluteUrl(pageContext, url);
}

export function getWebAbsoluteUrl(pageContext: IVssPageContext, path: string): Url.Uri {
    const tfsServiceRootPath = getServiceRootPath(pageContext, TfsServiceInstanceId);
    const tfsServiceRootPathUri = new Url.Uri(tfsServiceRootPath);
    // todo: seems hacky!
    tfsServiceRootPathUri.path = "";
    const webUrl = Url.combineUrlPaths(tfsServiceRootPathUri.absoluteUri, path);
    return new Url.Uri(webUrl);
}

export function getVersionedContentUrl(pageContext: IVssPageContext, contributedFilePath: string): string {
    return getAssetLocation(pageContext, contributedFilePath);
}

export function getPackageDependencyUrl(
    pageContext: IVssPageContext,
    feedName: string,
    packageName: string,
    protocolType: string
): string {
    const packageUrl: Url.Uri = getPackagingHubAbsoluteUrl(pageContext);
    packageUrl.addQueryParams(
        {
            _a: HubActionStrings.ViewPackage,
            feed: feedName,
            package: packageName,
            version: undefined,
            protocolType,
            view: PackageDetailsPivot.VERSIONS
        },
        true // replaceExisting
    );
    return decodeURIComponent(packageUrl.absoluteUri);
}

export function getPackageDetailsPageUrl(
    pageContext: IVssPageContext,
    feedName: string,
    pkg: Package,
    version: string,
    isRecycleBin: boolean = false
): string {
    const versionFound: boolean = pkg.versions.some(v => v.version === version);
    const selectedVersion: string = versionFound ? version : pkg.versions[0].version;
    const packageUrl: Url.Uri = getPackagingHubAbsoluteUrl(pageContext);
    packageUrl.addQueryParams(
        {
            _a: isRecycleBin ? HubActionStrings.RecycleBinPackage : HubActionStrings.ViewPackage,
            feed: feedName,
            package: pkg.name,
            version: selectedVersion,
            protocolType: pkg.protocolType
        },
        true // replaceExisting
    );
    return decodeURIComponent(packageUrl.absoluteUri);
}

export function getLatestPackageDetailsPageUrl(
    pageContext: IVssPageContext,
    feedId: string,
    pkg: Package,
    viewId: string = null,
    preferRelease: string = null
): string {
    if (viewId != null) {
        feedId = Utils_String.format("{0}@{1}", feedId, viewId);
    }
    const packageUrl: Url.Uri = getPackagingHubAbsoluteUrl(pageContext);
    packageUrl.addQueryParams(
        {
            _a: HubActionStrings.ViewPackage,
            feed: feedId,
            package: pkg.id,
            preferRelease
        },
        true // replaceExisting
    );
    return packageUrl.absoluteUri;
}

export function getPermissionSettingsUrl(pageContext: IVssPageContext, feedName: string): string {
    const packageUrl: Url.Uri = getPackagingHubAbsoluteUrl(pageContext);
    packageUrl.addQueryParams(
        {
            _a: HubActionStrings.Settings,
            feed: feedName,
            view: SettingsPivotKeys.permissions
        },
        true // replaceExisting
    );
    return decodeURIComponent(packageUrl.absoluteUri);
}

export function getAuditLogUrl(pageContext: IVssPageContext): string {
    const tfsUrl = getServiceRootPath(pageContext, TfsServiceInstanceId);
    const auditUrl = Url.combineUrlPaths(tfsUrl, "_settings", "audit");
    return auditUrl;
}

export function isAbsoluteUrl(url: string): boolean {
    if (!url) {
        return false;
    }

    url = url.toLowerCase();
    return url.indexOf("http:") === 0 || url.indexOf("https:") === 0 || url.indexOf("//") === 0;
}

/**
 * Approved list of URL schemes.
 */
export const SafeUriSchemes = [
    "http",
    "https",
    "ftp",
    "gopher",
    "mailto",
    "news",
    "telnet",
    "wais",
    "vstfs",
    "tfs",
    "alm",
    "mtm",
    "mtms",
    "mtr",
    "mtrs",
    "mfbclient",
    "mfbclients",
    "test-runner",
    "x-mvwit",
    "onenote",
    "codeflow",
    "file",
    "tel",
    "skype"
];

/**
 * Check if specified URL is safe - i.e. part of an approved list of URL schemes.
 *
 * @param url Url to check.
 * @returns {boolean}
 */
export function isSafeProtocol(url: string): boolean {
    const trimUrl = url.trim();
    const indexOfSchemeDelimiter = trimUrl.indexOf(":");
    if (indexOfSchemeDelimiter >= 0) {
        const scheme = trimUrl.substr(0, indexOfSchemeDelimiter);
        for (const safeUriScheme of SafeUriSchemes) {
            if (Utils_String.equals(scheme, safeUriScheme, true)) {
                return true;
            }
        }
    }

    return false;
}
